import React, { useEffect, useState } from 'react'
import Button from 'app/components/Button/Button'
import Bem from 'app/utils/bem-helper'
import { ClientDataLayer } from 'app/utils/DataLayer'
import { Form, Formik } from 'formik'
import { withCookies } from 'react-cookie'

import {
  supportTypeFieldsDefaults,
  businessTypeFieldsDefaults,
  handleFormSubmit,
  salesValidationSchema,
  validationSchema,
  submissionMap
} from './logic'
import Section from './Section'
import BusinessType from './Steps/BusinessType'
import ContactInfo from './Steps/ContactInfo'
import FinalMessage from './Steps/FinalMessage'
import SupportType from './Steps/SupportType'

import './ContactForm.scss'

const steps = ['Support Type', 'Business/Industry', 'Contact Info']

function ContactForm({
  showCaptcha = false,
  cookies,
  supportTypeFields = supportTypeFieldsDefaults,
  businessTypeFields = businessTypeFieldsDefaults,
  redirectUrl = '/secure/request-information/support/thankyou',
  submitTo,
  submitToEmail = null,
  formId
}) {
  const b = Bem('contact-form')
  const [disabled, setDisabled] = useState(false)
  const [showContinue, setShowContinue] = useState(true)
  const [completedSteps, setCompletedSteps] = useState([])
  const [activeStep, setActiveStep] = useState(0)
  const [editStep, setEditStep] = useState(0)
  const [supportType, setSupportType] = useState('')
  const [submissionError, setSubmissionError] = useState(null)
  const isLastStep = completedSteps.includes(steps.length - 1)
  const [currentValidationSchema, setCurrentValidationSchema] = useState(
    salesValidationSchema[activeStep]
  )
  const [v2Token, setV2Token] = useState(null)
  const [isUsingRecaptchaV2, setIsUsingRecaptchaV2] = useState(false)
  const [redirectTo, setRedirectTo] = useState(null)

  const injectRecaptchaScript = isRecaptchaV2 => {
    setSubmissionError(null)
    setIsUsingRecaptchaV2(isRecaptchaV2)
    const head = document.getElementsByTagName('head')[0]
    const script = document.createElement('script')
    const version = isRecaptchaV2 ? 'V2' : 'V3'
    script.setAttribute(
      'src',
      `https://www.google.com/recaptcha/api.js?onload=setupRecaptcha${version}&render=explicit`
    )
    head.append(script)
  }

  const verifyRecaptcha = async () => {
    return await window?.grecaptcha?.execute(0, { action: 'submit' })
  }

  useEffect(() => {
    if (showCaptcha) {
      window.setupRecaptchaV2 = () => {
        window.grecaptcha?.render('recaptcha-v2-badge', {
          sitekey: window?.RECAPTCHA_KEY_V2,
          callback: token => {
            setV2Token(token)
          },
          'expired-callback': () => {}
        })
      }

      window.setupRecaptchaV3 = () => {
        window.grecaptcha?.render('recaptcha-v3-badge', {
          badge: 'inline',
          sitekey: window?.RECAPTCHA_KEY_V3,
          size: 'invisible'
        })
      }
    }

    injectRecaptchaScript(false)

    return () => {
      delete window.setupRecaptchaV2
      delete window.setupRecaptchaV3
    }
  }, [])
  useEffect(() => {
    if (supportType === 'Sales') {
      setCurrentValidationSchema(salesValidationSchema[activeStep])
    } else {
      setCurrentValidationSchema(validationSchema[activeStep])
    }
  }, [activeStep, supportType])

  useEffect(() => {
    if (editStep === 0) {
      setShowContinue(true)
    } else {
      setShowContinue(false)
    }
  }, [editStep])

  function setLeadCookie(values, orderId) {
    try {
      let id = ''
      if (orderId) {
        id = orderId
      }
      const leadData = {
        leadFormName: 'leadform|contact-form',
        shawAccountNumber: values.shawAccountNumber || '',
        productInterest: values.productInterest || [],
        employeesCount: values.employeesCount || '',
        orderId: id
      }
      const leadDataString = JSON.stringify(leadData)
      const cookieOptions = {
        path: '/',
        expires: new Date(+new Date() + 12096e5)
      }
      cookies.set('leadData', leadDataString, cookieOptions)
    } catch (error) {
      if (window.LOG_LEVEL?.match(/info|verbose|debug|silly/)) {
        console.log('------- analytics cookie error --------->', error)
      }
    }
  }

  async function submitForm(values, actions) {
    setDisabled(true)
    setSubmissionError(null)

    let token = null
    try {
      values.submitTo = submitTo
      values.submitToEmail = submitToEmail
      values.useReCaptcha = showCaptcha
      values.formId = formId

      let response = null

      if (showCaptcha) {
        if (!isUsingRecaptchaV2) {
          token = await verifyRecaptcha()
          response = await handleFormSubmit({
            ...values,
            token,
            version: 'v3'
          })
        } else if (isUsingRecaptchaV2) {
          token = v2Token
          document
            ?.querySelector('button[type="submit"]')
            .removeAttribute('disabled')

          response = await handleFormSubmit({
            ...values,
            token,
            version: 'v2'
          })
        }
        if (process?.env?.LOG_LEVEL?.match(/info|verbose|debug|silly/)) {
          console.log(
            '------- response.data --------->',
            JSON.stringify(response && response.data)
          )
        }
        if (response?.data?.statusCode === 'FAILED') {
          actions.setSubmitting(false)
          setDisabled(false)
          throw new Error(
            `Form submission failed: ${response.data.statusDescription}`
          )
        }
      } else {
        response = await handleFormSubmit({ ...values })
        if (process?.env?.LOG_LEVEL?.match(/info|verbose|debug|silly/)) {
          console.log(
            '------- response.data --------->',
            JSON.stringify(response && response.data)
          )
        }
      }

      if (response) {
        const refId = response?.data?.id || response?.data?.data?.requestNumber
        actions.setSubmitting(false)
        setLeadCookie(values, refId)

        if (typeof window != 'undefined') {
          if (refId) {
            window.localStorage.setItem('refId', refId)
          }
          window.location.href = redirectUrl
        }
      }
    } catch (error) {
      if (process?.env?.LOG_LEVEL?.match(/info|verbose|debug|silly/)) {
        console.log('------- Contact Form Error --------->', error)
      }

      if (showCaptcha && !isUsingRecaptchaV2) {
        setIsUsingRecaptchaV2(true)
        await injectRecaptchaScript(true)
      }

      actions.setSubmitting(false)
      setDisabled(false)
      const cdl = new ClientDataLayer()
      cdl.sendError('Form Submission error')
      const errorVariant = showCaptcha
        ? 'selecting the reCaptcha checkbox and trying again'
        : 'again later'
      setSubmissionError(
        `Form submission was unsuccessful, please try ${errorVariant}.`
      )
    }
  }

  function handleSubmit(values, actions) {
    actions.setSubmitting(false)

    if (
      values.businessType &&
      values.supportType?.match(/technical support|cancel services/i) &&
      submissionMap[values.supportType].phone[values.businessType]
    ) {
      setCompletedSteps(
        Array.from(new Set([...completedSteps, 'Final Message']))
      )
    } else if (
      values.supportType?.match(
        /sales|technical support|account management|cancel services/i
      ) &&
      completedSteps.includes(steps[1])
    ) {
      submitForm(values, actions)
    } else if (isLastStep) {
      submitForm(values, actions)
    } else {
      setActiveStep(activeStep + 1)
      setCompletedSteps(
        Array.from(
          new Set([
            ...completedSteps.filter(s => s !== 'Final Message'),
            steps[activeStep]
          ])
        )
      )
      actions.setTouched({})
    }
  }

  function resetUi() {
    setCompletedSteps([])
    setActiveStep(0)
    setEditStep(0)
    setSupportType('')

    if (typeof window !== 'undefined') {
      const form = document.getElementById('contact-form')
      const y = form.offsetTop
      window.scrollTo(0, y)
    }
  }

  function alternativeUiFlow(values, step) {
    setEditStep(step)
    let newActiveStep = completedSteps.length

    if (values.supportType?.match(/sales/i)) {
      if (
        !values.businessType ||
        values.businessType?.match(
          /small and medium business|enterprise\/franchise|hotels/i
        ) === null
      ) {
        newActiveStep = 1
        setCompletedSteps([steps[0]])
      } else if (
        values.businessType?.match(
          /small and medium business|enterprise\/franchise|hotels/i
        ) !== null
      ) {
        newActiveStep = 2
        setCompletedSteps([steps[0], steps[1]])
      }
    } else if (values.supportType?.match(/account management/i)) {
      if (values.businessType?.match(/Community Living - Residents/i)) {
        newActiveStep = 1
        setCompletedSteps([steps[0], steps[1]])
      } else if (values.businessType) {
        const cSteps = Array.from(new Set([...completedSteps, steps[1]]))
        newActiveStep = 2
        setCompletedSteps(cSteps)
      }
    } else if (values.supportType?.match(/technical support/i)) {
      if (
        values.businessType?.match(
          /enterprise\/franchise|hotels|business satellite|partner channel/i
        )
      ) {
        newActiveStep = 2
        setCompletedSteps([steps[0], steps[1]])
      } else {
        const cSteps = Array.from(new Set([...completedSteps, steps[1]]))
        newActiveStep = values.businessType ? 2 : 1
        setCompletedSteps(cSteps)
      }
    } else if (values.supportType?.match(/cancel services/i)) {
      if (
        values.businessType?.match(
          /small and medium business|community living - residents/i
        )
      ) {
        newActiveStep = completedSteps.length
        setCompletedSteps([steps[0], steps[1]])
      } else if (values.businessType) {
        const cSteps = Array.from(new Set([...completedSteps, steps[1]]))
        newActiveStep = 2
        setCompletedSteps(cSteps)
      }
    }

    setActiveStep(newActiveStep)
    setEditStep(0)
  }

  function handleEdit(step) {
    setEditStep(step)
  }

  function buttonAnalytics(values, isSubmit) {
    return values.businessType && isSubmit
      ? `${values.supportType}|${values.businessType}|`
      : ''
  }

  return (
    <>
      <Formik
        enableReinitialize
        validationSchema={currentValidationSchema}
        validateOnMount={false}
        initialValues={{
          // required fields:
          supportType: '',
          businessType: '',
          firstName: '',
          lastName: '',
          phoneNum: '',
          emailAddy: '',
          companyName: '',
          city: '',
          province: '',
          details: '',
          extra: []
        }}
        onSubmit={handleSubmit}
      >
        {({
          values,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
          setTouched,
          resetForm,
          isSubmitting
        }) => {
          const message = values.supportType?.match(/sales/i)
            ? null
            : (completedSteps.length > 1 &&
                values.supportType &&
                values.businessType &&
                submissionMap[values.supportType].phone[values.businessType]) ||
              (completedSteps.length > 1 &&
                values.supportType &&
                values.businessType &&
                submissionMap[values.supportType].phone[values.requestType])
          const shortFlow = completedSteps.length > 1 && values.businessType
          const isSubmit = shortFlow || isLastStep

          return (
            <Form>
              <div className={b.e('all').classes()}>All fields required</div>
              <Section
                step={1}
                title={steps[0]}
                sectionTitle="What can we help you with today?"
                edit={handleEdit}
                editStep={editStep}
                completedValue={values.supportType}
                completed={
                  values.supportType && completedSteps.includes(steps[0])
                }
                columns={[1, 2, 4]}
                callbackOnSave={alternativeUiFlow}
                setTouched={setTouched}
                touched={touched}
                redirectTo={redirectTo}
              >
                <SupportType
                  onChange={handleChange}
                  setFieldValue={setFieldValue}
                  setSupportType={setSupportType}
                  fields={supportTypeFields}
                  values={values}
                  setRedirectTo={setRedirectTo}
                />
              </Section>
              {!redirectTo &&
                values.supportType &&
                completedSteps.includes(steps[0]) && (
                  <Section
                    step={2}
                    title={steps[1]}
                    sectionTitle="What type of business are you in?"
                    edit={handleEdit}
                    editStep={editStep}
                    completedValue={values.businessType}
                    completed={
                      values.businessType &&
                      ((completedSteps.includes(steps[0]) &&
                        completedSteps.includes(steps[1])) ||
                        completedSteps.includes('Final Message'))
                    }
                    callbackOnSave={alternativeUiFlow}
                    setTouched={setTouched}
                    touched={touched}
                    showBorder={!message}
                    redirectTo={redirectTo}
                  >
                    <BusinessType
                      onChange={e => {
                        handleChange(e)
                      }}
                      setFieldValue={setFieldValue}
                      fields={businessTypeFields}
                    />
                  </Section>
                )}
              {message && (
                <FinalMessage
                  message={message}
                  tracking={`${values.supportType}|${values.businessType}`}
                  resetForm={() => {
                    resetForm()
                    resetUi()
                  }}
                />
              )}
              {values.supportType.match(/sales/i) &&
                values.businessType &&
                completedSteps.includes(steps[1]) &&
                !message && (
                  <Section
                    step={3}
                    title={steps[2]}
                    sectionTitle="How can we reach you?"
                    edit={handleEdit}
                    editStep={editStep}
                    completed={false}
                    completedValue={true}
                    redirectTo={redirectTo}
                  >
                    <ContactInfo
                      values={values}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      setFieldValue={setFieldValue}
                    />
                  </Section>
                )}

              {!message && showContinue && (
                <Button
                  disabled={disabled}
                  target={
                    redirectTo
                      ? redirectTo?.match(/https/)
                        ? '_blank'
                        : '_self'
                      : null
                  }
                  link={redirectTo}
                  icon={
                    redirectTo?.match(/https/) ? { type: 'Link_Offsite' } : null
                  }
                  type={redirectTo ? 'link' : 'submit'}
                  className={b.e('continue').classes()}
                  text={isSubmit ? 'Submit request' : 'Continue'}
                  design="scotch-primary"
                  variant="light"
                  analyticsEvent={
                    isSubmit ? 'submitAction' : 'navigationAction'
                  }
                  analyticsValue={`business|contact-form|step-${
                    shortFlow ? 3 : activeStep + 1
                  }-${
                    shortFlow ? `${steps[2]}` : `${steps[activeStep]}`
                  }|${buttonAnalytics(values, isSubmit, shortFlow)}${
                    isSubmit ? 'Submit request' : 'Continue'
                  }`}
                />
              )}

              {submissionError && !isSubmitting && (
                <div className={b.e('submission-error').classes()}>
                  {submissionError}
                </div>
              )}
            </Form>
          )
        }}
      </Formik>
      <div className={'recaptcha-group'}>
        {isUsingRecaptchaV2 && (
          <div className={'recaptcha-badge'} id="recaptcha-v2-badge" />
        )}
        {!isUsingRecaptchaV2 && (
          <div className={'recaptcha-badge'} id="recaptcha-v3-badge" />
        )}
      </div>
    </>
  )
}

export default withCookies(ContactForm)
